<template>
    <UPopover
        :arrow="true"
        :content="{ align: 'center', side: 'top', sideOffset: 8 }"
        :disabled="!disabledReason"
        :ui="popoverUi"
    >
        <div class="relative" :class="ui?.wrapper">
            <div
                v-if="disabled && disabledReason"
                class="absolute inset-0 z-5 cursor-not-allowed"
            ></div>
            <UButton
                :class="[
                    // Component parity (Part E): DS .crane-btn shared interaction model —
                    // 1px lift on hover, 2% press on active, navy focus ring, brand easing.
                    'group crane-button cursor-pointer disabled:cursor-not-allowed',
                    'transition-[color,background-color,border-color,transform,box-shadow] duration-150 ease-[var(--btn-ease)]',
                    !isDashedBrand
                        ? 'hover:not-disabled:-translate-y-px hover:not-disabled:shadow-[var(--btn-shadow-hover)]'
                        : '',
                    !isDashedBrand
                        ? 'active:not-disabled:translate-y-px active:not-disabled:scale-[0.98] active:not-disabled:shadow-[var(--btn-shadow-press)]'
                        : '',
                    !isDashedBrand
                        ? 'focus-visible:!outline-none focus-visible:!shadow-[var(--btn-focus-ring),var(--btn-shadow-hover)]'
                        : 'focus-visible:!outline-none',
                    'motion-reduce:!transform-none motion-reduce:transition-colors',
                    !isOutlineVariant && !isGhostVariant && !brandClass
                        ? 'not-disabled:!bg-(--crane-button-bg-color) hover:not-disabled:!bg-[color-mix(in_srgb,var(--crane-button-bg-color)_88%,black)] active:not-disabled:!bg-[color-mix(in_srgb,var(--crane-button-bg-color)_78%,black)]'
                        : '',
                    isOutlineVariant && !brandClass
                        ? [
                              'not-disabled:text-(--crane-button-text-color) not-disabled:ring-(--crane-button-border-color)',
                              // Lighter hover; stronger press. `not-[&:active]` keeps hover from overriding :active.
                              'hover:not-[&:active]:not-disabled:!bg-[color-mix(in_srgb,var(--crane-button-bg-color)_10%,transparent)]',
                              'active:not-disabled:!bg-[color-mix(in_srgb,var(--crane-button-bg-color)_25%,transparent)]',
                          ].join(' ')
                        : '',
                    isGhostVariant && !brandClass
                        ? 'crane-button-ghost hover:not-disabled:!bg-[color-mix(in_srgb,var(--crane-button-bg-color)_25%,transparent)]'
                        : '',
                    brandClass,
                    // On UButton itself — scoped CSS does not win over brand `!bg-*` / Nuxt ghost disabled.
                    DISABLED_PAINT,
                    widthClass,
                    // MVP Loading Buttons 1c: keep width, fade label, center ring.
                    // Do not pass UButton `loading` here — that forces disabled
                    // (neutral paint) and a leading spinner beside the label.
                    hasLoadingOverlay
                        ? 'crane-button-loading-replace relative pointer-events-none'
                        : '',
                    props.class,
                    ui?.root || '',
                ]"
                :style="buttonStyle"
                :type="type"
                :size="size"
                :variant="resolvedVariant"
                :loading="isNativeLoading"
                :disabled="disabled"
                :aria-busy="isBusy || undefined"
                v-bind="$attrs"
                @click="$emit('click', $event)"
            >
                <template v-if="useLoadingReplace">
                    <CraneLoadingSpinner
                        v-if="isLoadingReplace"
                        class="crane-button-loading-spinner"
                    />
                    <!--
                      inline-flex keeps multi-node default slots (label + icon)
                      horizontal; opacity-0 holds width while the ring centers.
                    -->
                    <span
                        class="crane-button-label inline-flex items-center gap-1.5"
                        :class="isLoadingReplace ? 'opacity-0' : undefined"
                        :aria-hidden="isLoadingReplace || undefined"
                    >
                        <slot />

                        <AppIcon v-if="icon" :name="icon" :class="iconClass" size="16" />
                    </span>
                </template>
                <slot v-else />
                <div v-if="icon" class="relative">
                    <AppIcon :name="icon" :class="iconClass" size="16" />
                    <CraneLoadingSpinner
                        v-if="isLoadingIcon"
                        class="crane-button-loading-spinner"
                    />
                </div>
            </UButton>
        </div>

        <template #content>
            <p class="px-4 py-3 text-sm">{{ disabledReason || '' }}</p>
        </template>
    </UPopover>
</template>

<script setup lang="ts">
/**
 * CraneButton is a wrapper around the UButton component that adds a disabled reason popover.
 * It is used to ensure that the button is disabled when the disabled prop is true while
 * still allowing the button area to be clicked, which shows the disabled reason popover.
 *
 * Icons: pass `icon` for a trailing affordance (e.g. CTA chevron).
 */
import type { HTMLAttributes } from 'vue'
import type { UButton } from '.nuxt/components'
import AppIcon from '~~/layers/base/components/shared/AppIcon.vue'
import CraneLoadingSpinner from '@/components/crane/LoadingSpinner.vue'

type UButtonSize = InstanceType<typeof UButton>['$props']['size']
type UButtonType = InstanceType<typeof UButton>['$props']['type']
type UButtonVariant = InstanceType<typeof UButton>['$props']['variant']
type UButtonLoading = InstanceType<typeof UButton>['$props']['loading']
type UButtonDisabled = InstanceType<typeof UButton>['$props']['disabled']
type UButtonIcon = InstanceType<typeof UButton>['$props']['icon']

/**
 * Width model aligned to DS `.crane-btn` / `.crane-btn--fluid`:
 * - `fixed`: hug content, never narrower than 260px
 * - `fluid`: full width (Capacitor stays mobile-width; no `md:w-auto` hug so
 *   existing full-width CTAs do not shrink on wide viewports)
 * Omit (default) so existing call sites keep controlling width via `class`.
 */
type CraneButtonWidth = 'fixed' | 'fluid'

/**
 * DS brand recipes from `.crane-btn--*` (Group 1–2 + Nuxt semantic tones).
 * Opt-in only — existing `variant` / `color` callers stay unchanged.
 * Outline recipes use a real 1px border (not Nuxt UI ring outline).
 * Tone solids/outlines (`secondary` / `success` / `warning` / `error`) paint
 * Crane ramps; `primary`/`info` use `navy-*`. CTA green stays `green-on-*`.
 */
type CraneButtonBrand =
    | 'green-on-dark'
    | 'white-solid'
    | 'white-outline'
    | 'green-on-light'
    | 'navy-solid'
    | 'navy-outline'
    | 'secondary-solid'
    | 'secondary-outline'
    | 'success-solid'
    | 'success-outline'
    | 'warning-solid'
    | 'warning-outline'
    | 'error-solid'
    | 'error-outline'
    | 'dashed'

/** Shared base for brand recipes — DS `--radius` (4px), no Nuxt ring. */
const BRAND_BASE = 'crane-button-brand ring-0 rounded border border-solid'

/**
 * Disabled paint — same for every brand + CTA surface.
 * Must be Tailwind on UButton (scoped CSS does not land on its root).
 */
const DISABLED_PAINT = [
    'disabled:!bg-(--neutral-400) disabled:!border-(--neutral-400)',
    'disabled:!text-(--neutral-50) disabled:!opacity-100',
    'aria-disabled:!bg-(--neutral-400) aria-disabled:!border-(--neutral-400)',
    'aria-disabled:!text-(--neutral-50) aria-disabled:!opacity-100',
].join(' ')

/**
 * Brand fills / borders / hovers (DS Group 1–2 + semantic tones).
 * Enabled fills use `not-disabled:` so they never fight disabled paint.
 * Error solid `aria-busy` holds the hover step (confirm busy / Loading Buttons 1c).
 */
const BRAND_RECIPES: Record<CraneButtonBrand, string> = {
    'green-on-dark': [
        BRAND_BASE,
        'not-disabled:!bg-(--action-700) not-disabled:text-white not-disabled:border-(--action-700)',
        'hover:not-disabled:!bg-(--action-800) hover:not-disabled:border-(--action-800)',
    ].join(' '),
    'white-solid': [
        BRAND_BASE,
        'not-disabled:!bg-white not-disabled:text-(--primary-700) not-disabled:border-white',
        'hover:not-disabled:!bg-[rgba(255,255,255,0.92)] hover:not-disabled:border-[rgba(255,255,255,0.92)]',
    ].join(' '),
    'white-outline': [
        BRAND_BASE,
        'crane-button-ghost not-disabled:text-white not-disabled:border-[rgba(255,255,255,0.55)]',
        'hover:not-disabled:border-white hover:not-disabled:!bg-[rgba(255,255,255,0.08)]',
    ].join(' '),
    'green-on-light': [
        BRAND_BASE,
        'not-disabled:!bg-(--action-800) not-disabled:text-white not-disabled:border-(--action-800)',
        'hover:not-disabled:!bg-(--action-900) hover:not-disabled:border-(--action-900)',
    ].join(' '),
    'navy-solid': [
        BRAND_BASE,
        'not-disabled:!bg-(--primary-700) not-disabled:text-white not-disabled:border-(--primary-700)',
        'hover:not-disabled:!bg-(--cta-navy-hover) hover:not-disabled:border-(--cta-navy-hover)',
    ].join(' '),
    'navy-outline': [
        BRAND_BASE,
        'crane-button-ghost not-disabled:text-(--primary-700) not-disabled:border-[rgba(15,23,43,0.55)]',
        'hover:not-disabled:border-(--primary-700) hover:not-disabled:!bg-[rgba(15,23,43,0.05)]',
    ].join(' '),
    'secondary-solid': [
        BRAND_BASE,
        'not-disabled:!bg-(--secondary-700) not-disabled:text-white not-disabled:border-(--secondary-700)',
        'hover:not-disabled:!bg-(--secondary-800) hover:not-disabled:border-(--secondary-800)',
    ].join(' '),
    'secondary-outline': [
        BRAND_BASE,
        'crane-button-ghost not-disabled:text-(--secondary-700) not-disabled:border-[rgba(68,19,32,0.55)]',
        'hover:not-disabled:border-(--secondary-700) hover:not-disabled:!bg-[rgba(68,19,32,0.05)]',
    ].join(' '),
    'success-solid': [
        BRAND_BASE,
        'not-disabled:!bg-(--success-700) not-disabled:text-white not-disabled:border-(--success-700)',
        'hover:not-disabled:!bg-(--tertiary-800) hover:not-disabled:border-(--tertiary-800)',
    ].join(' '),
    'success-outline': [
        BRAND_BASE,
        'crane-button-ghost not-disabled:text-(--success-700) not-disabled:border-(--success-700)',
        'hover:not-disabled:!bg-(--success-700) hover:not-disabled:text-white hover:not-disabled:border-(--success-700)',
    ].join(' '),
    'warning-solid': [
        BRAND_BASE,
        'not-disabled:!bg-(--warning-700) not-disabled:text-white not-disabled:border-(--warning-700)',
        'hover:not-disabled:!bg-(--warning-800) hover:not-disabled:border-(--warning-800)',
    ].join(' '),
    'warning-outline': [
        BRAND_BASE,
        'crane-button-ghost not-disabled:text-(--warning-700) not-disabled:border-(--warning-700)',
        'hover:not-disabled:!bg-(--warning-700) hover:not-disabled:text-white hover:not-disabled:border-(--warning-700)',
    ].join(' '),
    'error-solid': [
        BRAND_BASE,
        'not-disabled:!bg-(--error-700) not-disabled:text-white not-disabled:border-(--error-700)',
        'hover:not-disabled:!bg-(--error-800) hover:not-disabled:border-(--error-800)',
        'aria-busy:!bg-(--error-800) aria-busy:!border-(--error-800)',
    ].join(' '),
    'error-outline': [
        BRAND_BASE,
        'crane-button-ghost not-disabled:text-(--error-700) not-disabled:border-(--error-700)',
        'hover:not-disabled:!bg-(--error-700) hover:not-disabled:text-white hover:not-disabled:border-(--error-700)',
    ].join(' '),
    /**
     * `.pf-addr-add` — dashed idle, gold hover/press (border, type, tint).
     * Paint only: no lift, press scale, or shadow. Skip `crane-button-ghost`
     * so scoped fill cannot hide `--gold-tint`.
     */
    dashed: [
        'crane-button-brand ring-0 rounded border border-dashed',
        'not-disabled:!bg-transparent not-disabled:!text-(--neutral-700) not-disabled:!border-(--neutral-300)',
        'hover:not-disabled:!border-(--gold) hover:not-disabled:!text-(--gold) hover:not-disabled:!bg-(--gold-tint)',
        'active:not-disabled:!border-(--gold) active:not-disabled:!text-(--gold) active:not-disabled:!bg-(--gold-tint)',
    ].join(' '),
}

interface Props {
    /**
     * Classes on the underlying UButton (not the popover wrapper).
     * Accepts the same values as Vue `:class` (string, array, object map).
     */
    class?: HTMLAttributes['class']
    /** UI prop for the button and the surrounding div */
    ui?: { wrapper?: string; root?: string }
    /** CSS variable for background color (e.g., 'var(--color-primary)' or '--color-primary') */
    color?: string | null
    /** Size of the button (mirrors UButton size prop) */
    size?: UButtonSize
    /** Button type (mirrors UButton type prop, e.g. 'submit') */
    type?: UButtonType
    /** Variant of the button (mirrors UButton variant prop) */
    variant?: UButtonVariant
    /** Loading state of the button (mirrors UButton loading prop) */
    loading?: UButtonLoading
    /**
     * When true with `loading`, fade the label (keep width) and center an 18px
     * ring spinner. Matches Handwriting App v2 confirm busy / Loading Buttons 1c.
     * Uses `aria-busy` + `pointer-events-none`, not `disabled` (disabled means
     * unavailable). Does not use UButton `loading` (that forces disabled +
     * leading spinner beside the label).
     */
    loadingReplace?: boolean
    /** Disabled state of the button (mirrors UButton disabled prop) */
    disabled?: UButtonDisabled
    /** Reason for disabling the button (e.g. 'Insufficient funds') */
    disabledReason?: string | null
    /**
     * Trailing Nuxt Icon name (e.g. `lucide:arrow-right`).
     */
    icon?: UButtonIcon
    /**
     * When true (default), trailing icons nudge +3px on hover/active (CTA chevron).
     * Set false for static icons (upload, edit, trash).
     */
    iconNudge?: boolean
    /**
     * Opt-in DS width mode. Default `undefined` applies no width classes so
     * current callers (many with their own `w-full` / compact layouts) are unchanged.
     */
    width?: CraneButtonWidth | null
    /**
     * Opt-in DS brand recipe (On Dark / On Light groups).
     * Prefer this over Nuxt `variant="outline"` when matching `.crane-btn--*`.
     */
    brand?: CraneButtonBrand | null
}

const props = withDefaults(defineProps<Props>(), {
    color: null,
    size: undefined,
    type: undefined,
    variant: 'solid',
    loading: undefined,
    loadingReplace: false,
    disabled: undefined,
    disabledReason: undefined,
    icon: undefined,
    iconNudge: true,
    class: undefined,
    ui: undefined,
    width: null,
    brand: null,
})

defineEmits<{
    (e: 'click', event: MouseEvent): void
}>()

/** Computed value for the button background color */
const buttonBgColor = computed(() => {
    // Use provided color, or default to --ui-primary-highlighted
    const color = props.color || '--ui-primary-highlighted'

    // Normalize CSS variable format - ensure it starts with 'var(' if it's a variable name
    return color.startsWith('--') ? `var(${color}, var(--ui-bg-primary))` : color
})

/** Computed value for the button text color (used in outline variant) */
const buttonTextColor = computed(() => {
    // Use provided color, or default to --ui-primary-highlighted
    const color = props.color || '--ui-primary-highlighted'

    // Normalize CSS variable format - ensure it starts with 'var(' if it's a variable name
    return color.startsWith('--') ? `var(${color}, var(--ui-text-primary))` : color
})

/** Computed value for the button border color (used in outline variant) */
const buttonBorderColor = computed(() => {
    // Use provided color, or default to --ui-primary-highlighted
    const color = props.color || '--ui-primary-highlighted'

    // Normalize CSS variable format - ensure it starts with 'var(' if it's a variable name
    return color.startsWith('--') ? `var(${color}, var(--ui-border-primary))` : color
})

/**
 * Brand recipes force a transparent UButton shell so Nuxt outline rings are not used.
 */
const resolvedVariant = computed(() => (props.brand ? 'ghost' : props.variant))

/** Dashed recipe is paint-only — skip the shared lift / press / shadow model. */
const isDashedBrand = computed(() => props.brand === 'dashed')

/** Whether this is an outline variant button */
const isOutlineVariant = computed(() => {
    return resolvedVariant.value === 'outline'
})

/** Whether this is a ghost variant button */
const isGhostVariant = computed(() => {
    return resolvedVariant.value === 'ghost'
})

/** DS `.crane-btn--*` class string for the selected brand recipe. */
const brandClass = computed(() => {
    if (!props.brand) {
        return ''
    }
    return BRAND_RECIPES[props.brand]
})

/** Fixed / fluid width classes — empty when `width` is unset. */
const widthClass = computed(() => {
    if (props.width === 'fixed') {
        return 'min-w-[260px] w-auto'
    }
    if (props.width === 'fluid') {
        return 'w-full min-w-0'
    }
    return ''
})

/** Busy when loading; `loadingReplace` fades the label and centers the ring. */
const isBusy = computed(() => !!props.loading)
/** Only use `loadingReplace` when there is no icon and `loading` is true. */
const useLoadingReplace = computed(() => !!props.loadingReplace && !props.icon)
const isLoadingReplace = computed(() => isBusy.value && useLoadingReplace.value)
/** Busy with an icon: the ring spins over the faded icon instead of the label. */
const isLoadingIcon = computed(() => isBusy.value && !!props.icon)
/**
 * Either in-button spinner treatment. Both swap the button into its inert busy
 * state (`relative` positions the ring, `pointer-events-none` blocks clicks).
 */
const hasLoadingOverlay = computed(() => isLoadingReplace.value || isLoadingIcon.value)
/** Native UButton spinner only when not using loadingReplace. */
const isNativeLoading = computed(() => isBusy.value && !useLoadingReplace.value && !props.icon)

const iconClass = computed(() => {
    return [
        isLoadingIcon.value ? 'opacity-0' : undefined,
        props.iconNudge
            ? 'group-not-disabled:group-hover:translate-x-[3px] group-not-disabled:group-active:translate-x-[3px] transition-transform duration-220 ease-(--btn-ease)'
            : undefined,
        'size-4',
    ]
})

/** Computed style object for the button */
const buttonStyle = computed(() => {
    const style: Record<string, string> = {
        '--crane-button-bg-color': buttonBgColor.value,
    }

    // For outline variant, also set text and border colors
    if (isOutlineVariant.value) {
        style['--crane-button-text-color'] = buttonTextColor.value
        style['--crane-button-border-color'] = buttonBorderColor.value
    }

    return style
})

/** UI prop for the popover */
const popoverUi = computed(() => ({
    content: `${!props.disabled || !props.disabledReason ? 'hidden ' : ''}bg-(--white) ring-(--neutral-200) rounded`,
    arrow: 'fill-(--white) transform-scale-[1.5]',
}))
</script>

<style scoped>
/* Brand recipes own their fill via Tailwind / crane-button-ghost — skip the color-var paint. */
.crane-button:not(.crane-button-brand):not(:disabled) {
    background-color: var(--crane-button-bg-color) !important;
}

.crane-button-outline:not(:disabled) {
    color: var(--crane-button-text-color) !important;
    border-color: var(--crane-button-border-color) !important;
}

.crane-button-ghost:not(:disabled) {
    background-color: transparent !important;
}

/*
  loadingReplace placement only — the ring itself is styled by CraneLoadingSpinner.
  Negative margins re-use the spinner's own diameter var so the two stay in sync.
*/
.crane-button-loading-spinner {
    position: absolute;
    left: 50%;
    top: 50%;
    margin: calc(var(--crane-loading-spinner-size) / -2) 0 0
        calc(var(--crane-loading-spinner-size) / -2);
}
</style>
